import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/apiService';
import {
  CreditCard, Key, BarChart3, Zap, Database, Boxes, Activity,
} from 'lucide-react';
import { StatRow, Stat, StatSkeleton } from '../ui/StatRow';

interface DashboardData {
  creditBalance: number;
  totalCreditsUsed: number;
  apiKeysCount: number;
  activeKeysCount: number;
}

interface TDUsageData {
  plan: {
    displayName: string;
    freeTaskDetQuota: number;
  };
  freeQuotaRemaining: number;
  currentPeriodCount: number;
  totalLifetime: number;
  nextResetDate: string | null;
  isAdmin: boolean;
}

// ─── Dashboard ────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [tdUsage, setTdUsage] = useState<TDUsageData | null>(null);
  const [customModelCount, setCustomModelCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const subscriptionsEnabled = process.env.REACT_APP_ENABLE_SUBSCRIPTIONS !== 'false';

  useEffect(() => {
    const load = async () => {
      try {
        const [dashboardData, tdData, modelsData] = await Promise.all([
          apiService.getDashboard(),
          subscriptionsEnabled ? apiService.getTaskDetUsage().catch(() => null) : Promise.resolve(null),
          apiService.get('/portal/models/custom').then(r => {
            const arr = Array.isArray(r) ? r : (r?.data ?? []);
            return arr.length;
          }).catch(() => null),
        ]);
        setData(dashboardData);
        setTdUsage(tdData);
        setCustomModelCount(modelsData);
      } catch (err: any) {
        setError(err.message || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (error) {
    return (
      <div className="pk-note pk-note-danger">
        Error: {error}
      </div>
    );
  }

  const freeQuotaTotal = tdUsage?.plan?.freeTaskDetQuota ?? 0;
  const freeQuotaUsed = freeQuotaTotal - (tdUsage?.freeQuotaRemaining ?? 0);

  const formatDate = (iso: string | null) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const planLabel = tdUsage?.isAdmin
    ? 'Unlimited'
    : (tdUsage?.plan?.displayName ?? '—');

  return (
    <div>
      <div className="pk-page-head">
        <div className="col">
          <h1 className="pk-h1">Dashboard</h1>
          <p className="pk-sub">Your portal at a glance</p>
        </div>
      </div>

      {loading ? (
        <>
          <StatRow>
            {Array.from({ length: subscriptionsEnabled ? 4 : 3 }).map((_, i) => <StatSkeleton key={i} />)}
          </StatRow>
          {subscriptionsEnabled && (
            <div style={{ marginTop: 16 }}>
              <StatRow>
                {Array.from({ length: 4 }).map((_, i) => <StatSkeleton key={i} />)}
              </StatRow>
            </div>
          )}
        </>
      ) : (
        <>
          {subscriptionsEnabled && (
            <StatRow>
              {/* 1 — Current Plan */}
              <Stat
                icon={<Zap size={17} />}
                title="Current Plan"
                value={
                  tdUsage?.isAdmin
                    ? <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>Unlimited <span className="pk-badge pk-badge-accent">Admin</span></span>
                    : planLabel
                }
                subtitle={tdUsage?.nextResetDate ? `Resets ${formatDate(tdUsage.nextResetDate)}` : 'Manage subscription'}
                to="/plans"
              />

              {/* 2 — TD Free Quota */}
              <Stat
                icon={<Database size={17} />}
                title="Task Det. Free Quota"
                value={tdUsage ? `${(tdUsage.freeQuotaRemaining ?? 0).toLocaleString()} left` : '—'}
                subtitle={freeQuotaTotal > 0 ? `of ${freeQuotaTotal.toLocaleString()} this period` : 'This billing period'}
                to="/plans"
                progress={freeQuotaTotal > 0 ? { used: freeQuotaUsed, total: freeQuotaTotal } : undefined}
              />

              {/* 3 — TDs Used This Period */}
              <Stat
                icon={<BarChart3 size={17} />}
                title="Task Dets. Used"
                value={tdUsage ? (tdUsage.currentPeriodCount ?? 0).toLocaleString() : '—'}
                subtitle="This billing period"
                to="/plans"
              />

              {/* 4 — Lifetime TDs */}
              <Stat
                icon={<Activity size={17} />}
                title="Lifetime Task Dets."
                value={tdUsage ? (tdUsage.totalLifetime ?? 0).toLocaleString() : '—'}
                subtitle="All-time total"
                to="/plans"
              />
            </StatRow>
          )}

          <div style={{ marginTop: subscriptionsEnabled ? 16 : 0 }}>
            <StatRow variant={subscriptionsEnabled ? undefined : '3'}>
              {subscriptionsEnabled && (
                /* 5 — Credit Balance */
                <Stat
                  icon={<CreditCard size={17} />}
                  title="Credit Balance"
                  value={`$${(data?.creditBalance ?? 0).toFixed(4)}`}
                  subtitle="Available to spend"
                  to="/plans"
                />
              )}

              {/* 6 — Usage Analytics */}
              <Stat
                icon={<BarChart3 size={17} />}
                title="Usage Analytics"
                value={`$${(data?.totalCreditsUsed ?? 0).toFixed(4)}`}
                subtitle="Spent lifetime"
                to="/usage"
              />

              {/* 7 — API Keys */}
              <Stat
                icon={<Key size={17} />}
                title="API Keys"
                value={data ? `${data.activeKeysCount} active` : '—'}
                subtitle={data ? `${data.apiKeysCount} total` : 'Manage access'}
                to="/api-keys"
              />

              {/* 8 — Custom Models */}
              <Stat
                icon={<Boxes size={17} />}
                title="Custom Models"
                value={customModelCount !== null ? customModelCount.toLocaleString() : '—'}
                subtitle="Configured endpoints"
                to="/models"
              />
            </StatRow>
          </div>
        </>
      )}
    </div>
  );
}
